import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "index", "onClick", "onKeyDown", "onSortChange", "sortDirection", "sortOrder"];

var _TABLE_SORT_ICONS;

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { StyledTableHeaderCell } from './TableHeaderCell';
import { StyledIconArrowDown, StyledIconArrowUp, StyledIconSort, StyledSortIconWrapper, StyledTableHeaderCellLabelWrapper } from './TableSortIcon';
import { TABLE_SIZES, TABLE_SORT_DIRECTIONS } from '../../constants/TABLE';
import { KEY_CODES } from '../../constants/KEY_CODES';
import { TableSortableHeaderCellMixin } from '../../mixins/components/TableSortableHeaderCellMixin';
import { TableSortableContext } from '../../js/TableSortableContext';
import { TableContext } from '../../js/TableContext';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden';
var TABLE_SORT_ICONS = (_TABLE_SORT_ICONS = {}, _TABLE_SORT_ICONS[TABLE_SORT_DIRECTIONS.ascending] = StyledIconArrowUp, _TABLE_SORT_ICONS[TABLE_SORT_DIRECTIONS.descending] = StyledIconArrowDown, _TABLE_SORT_ICONS[TABLE_SORT_DIRECTIONS.none] = StyledIconSort, _TABLE_SORT_ICONS);

var getNextSortDirection = function getNextSortDirection(currentSortDirection, sortOrder) {
  var currentDirectionIndex = sortOrder.indexOf(currentSortDirection);
  var nextSortDirectionIndex = (currentDirectionIndex + 1) % sortOrder.length;
  return sortOrder[nextSortDirectionIndex];
};

export var StyledTableSortableHeaderCell = /*#__PURE__*/styled(StyledTableHeaderCell).withConfig({
  displayName: "StyledTableSortableHeaderCell",
  componentId: "c11n-8-64-0__sc-upcqoh-0"
})(["", ""], TableSortableHeaderCellMixin);
/**
 * `Table.SortableHeaderCell` is used in place of `Table.HeaderCell` when a column is sortable.
 *
 * A double-arrow icon appears on hover to indicate a column is sortable. A single-arrow icon
 * indicates which column is currently being sorted on and in which direction.
 *
 * This component is normally used with the
 * [`Table.Sortable`](#/Table%20components?id=tablesortable) HOC.
 *
 * **This component should not be used outside of the context of `Table`.**
 *
 * @since 8.41.0
 */

var TableSortableHeaderCell = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var children = props.children,
      index = props.index,
      onClick = props.onClick,
      onKeyDown = props.onKeyDown,
      onSortChange = props.onSortChange,
      sortDirection = props.sortDirection,
      sortOrder = props.sortOrder,
      rest = _objectWithoutProperties(props, _excluded); // Text aligment vars


  var tableContext = React.useContext(TableContext);
  var textAlign = props.align || tableContext.alignments && tableContext.alignments[index]; // Sort direction vars

  var sortableContext = React.useContext(TableSortableContext);
  var currentSortDirection = sortDirection || sortableContext.directions && sortableContext.directions[index] || TABLE_SORT_DIRECTIONS.none;
  var SortIcon = TABLE_SORT_ICONS[currentSortDirection]; // Use the general `onSortChange` provided by `Table.Sortable` or override if `onSortChange`
  // has been set on this cell

  var onSortCallback = onSortChange || sortableContext.onSortChange && sortableContext.onSortChange || null;
  var onClickCallback = React.useCallback(function (e) {
    var nextSortDirection = getNextSortDirection(currentSortDirection, sortOrder); // Update `Table.Sortable`'s state with new sort directions array

    if (typeof sortableContext.directions !== 'undefined') {
      var newDirections = new Array(sortableContext.directions.length).fill(TABLE_SORT_DIRECTIONS.none);
      newDirections[index] = nextSortDirection;
      sortableContext.setDirections(newDirections);
    } // Then execute callbacks
    // We're separating onSortChange from onClick to give us flexibility.
    // ex: In a future change, we might want to apply the sorting callback to an
    // icon button instead of the entire table cell.


    if (typeof onSortCallback === 'function') {
      onSortCallback({
        currentSortDirection: currentSortDirection,
        nextSortDirection: nextSortDirection
      }, props);
    }

    if (typeof onClick === 'function') {
      onClick(e);
    }
  }, [currentSortDirection, sortableContext, index, onClick, onSortCallback, sortOrder, props]);
  var onKeyDownCallback = React.useCallback(function (e) {
    if (e.keyCode === KEY_CODES.ENTER || e.keyCode === KEY_CODES.SPACE) {
      onClickCallback();
      e.preventDefault();
    }

    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }, [onClickCallback, onKeyDown]);
  return /*#__PURE__*/React.createElement(StyledTableSortableHeaderCell, _extends({
    align: textAlign,
    "aria-sort": TABLE_SORT_DIRECTIONS[currentSortDirection],
    as: "th",
    onClick: onClickCallback,
    onKeyDown: onKeyDownCallback,
    ref: ref,
    scope: "col",
    size: tableContext.size,
    sortDirection: currentSortDirection
  }, rest), /*#__PURE__*/React.createElement(StyledTableHeaderCellLabelWrapper, {
    role: "button",
    tabIndex: "0"
  },
  /* Uses &#65279; and white-space: nowrap on StyledSortIconWrapper to
      protect against the icon wrapping to the next line by itself. */
  textAlign === 'right' && SortIcon && /*#__PURE__*/React.createElement(StyledSortIconWrapper, null, /*#__PURE__*/React.createElement(SortIcon, null), "\uFEFF"), children, textAlign !== 'right' && SortIcon && /*#__PURE__*/React.createElement(StyledSortIconWrapper, null, "\uFEFF", /*#__PURE__*/React.createElement(SortIcon, null)), /*#__PURE__*/React.createElement(VisuallyHidden, null, currentSortDirection === TABLE_SORT_DIRECTIONS.none ? ', sortable column' : ", sorted " + TABLE_SORT_DIRECTIONS[currentSortDirection])));
});
TableSortableHeaderCell.displayName = 'Table.SortableHeaderCell';
TableSortableHeaderCell.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Usually, this is the header label text.
   */
  children: PropTypes.node,

  /**
   * Provided by `Table.Row`. Helps this cell read its alignment from the parent
   * `Table` alignments array.
   *
   * @ignore
   */
  index: PropTypes.number,

  /**
   * @ignore
   */
  onClick: PropTypes.func,

  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,

  /**
   * Callback fired on click or keypress of this header. Called before `onClick`.
   *
   * Takes two arguments: an object `{currentSortDirection, nextSortDirection}` and `props`
   *
   * Overrides `onSortChange` set in the parent
   * [`Table.Sortable`](#/Table%20components?id=tablesortable) component.
   */
  onSortChange: PropTypes.func,

  /**
   * Table size passed via Table's context. Used by the mixin to calculate spacing.
   *
   * @ignore
   */
  size: PropTypes.oneOf(Object.keys(TABLE_SIZES)),

  /**
   * The direction this column is currently sorted by.
   *
   * If included, indicates that this column is sortable and adds the appropriate
   * hover styles.
   *
   * This value is automatically set when you use
   * [`Table.Sortable`](#/Table%20components?id=tablesortable).
   */
  sortDirection: PropTypes.oneOf(Object.keys(TABLE_SORT_DIRECTIONS)),

  /**
   * An array that dictates the order of sort directions.
   *
   * When you first click on a sortable header cell, the sort direction is determined by
   * the first value in the array. Clicking on the header cell again sorts in the
   * direction determined by the second value and so on. Once you reach the end of the
   * array, we rotate back to the first value.
   *
   * If you click on a different header cell
   * and then come back to this one, the sequence begins again with the first value in the
   * array.
   */
  sortOrder: PropTypes.arrayOf(PropTypes.string)
} : {};
TableSortableHeaderCell.defaultProps = {
  sortOrder: [TABLE_SORT_DIRECTIONS.ascending, TABLE_SORT_DIRECTIONS.descending]
};
/** @component */

export default TableSortableHeaderCell;